/**
 * Registration form utility functions
 *
 * Shared helpers for registration form validation and state management
 */

interface AdultFormData {
  id: string;
  firstName: string;
  lastName: string;
  birthMonth: string;
  birthYear: string;
  phone: string;
  gender: string;
  relationshipToChild: string;
}

interface ChildFormData {
  id: string;
  firstName: string;
  lastName: string;
  birthMonth: string;
  birthYear: string;
  gender: string;
}

interface RegistrationFormData {
  adults?: AdultFormData[];
  children?: ChildFormData[];
  homePostalCode?: string;
  isLowIncome?: boolean | null;
  isPregnant?: boolean;
  dueMonth?: string;
  dueYear?: string;
  numberOfBabies?: number;
  languagesSpoken?: string[];
  otherLanguage?: string;
  hearAboutUs?: string[];
  hearAboutUsOther?: string;
  referralDetails?: string;
  privacyPolicyAccepted?: boolean;
}

/**
 * Checks if an adult has any meaningful data entered
 *
 * @param adult - Adult form data to check
 * @returns True if adult has any non-empty fields
 */
const hasAdultData = (adult: AdultFormData): boolean => {
  return Boolean(
    adult.firstName || adult.lastName || adult.phone || adult.birthMonth || adult.birthYear || adult.gender,
  );
};

/**
 * Checks if a child has any meaningful data entered
 *
 * @param child - Child form data to check
 * @returns True if child has any non-empty fields
 */
const hasChildData = (child: ChildFormData): boolean => {
  return Boolean(child.firstName || child.lastName || child.birthMonth || child.birthYear || child.gender);
};

/**
 * Determines if the registration form contains meaningful user input
 *
 * This is used to:
 * 1. Decide whether to restore draft data from localStorage
 * 2. Show confirmation dialog when user tries to leave with unsaved data
 * 3. Determine if form should be auto-saved
 *
 * @param formData - Current or draft form data
 * @returns True if form contains any user input beyond default values
 *
 * @example
 * // Check current form state
 * const hasData = hasRegistrationFormInput({
 *   adults,
 *   children,
 *   homePostalCode,
 *   // ... other fields
 * });
 *
 * @example
 * // Check saved draft
 * const draft = JSON.parse(localStorage.getItem('draft'));
 * if (hasRegistrationFormInput(draft)) {
 *   restoreDraft(draft);
 * }
 */
export const hasRegistrationFormInput = (formData: RegistrationFormData): boolean => {
  const {
    adults = [],
    children = [],
    homePostalCode,
    isLowIncome,
    isPregnant,
    dueMonth,
    dueYear,
    numberOfBabies,
    languagesSpoken = [],
    otherLanguage,
    hearAboutUs = [],
    hearAboutUsOther,
    referralDetails,
    privacyPolicyAccepted,
  } = formData;

  return (
    // Check if any adult has data beyond empty defaults
    adults.some(hasAdultData) ||
    // Check if any child exists with data
    (children.length > 0 && children.some(hasChildData)) ||
    // Check other form fields
    Boolean(homePostalCode) ||
    isLowIncome !== null ||
    Boolean(isPregnant) ||
    Boolean(dueMonth) ||
    Boolean(dueYear) ||
    (numberOfBabies !== undefined && numberOfBabies !== 1) ||
    languagesSpoken.length > 0 ||
    Boolean(otherLanguage) ||
    hearAboutUs.length > 0 ||
    Boolean(hearAboutUsOther) ||
    Boolean(referralDetails) ||
    Boolean(privacyPolicyAccepted)
  );
};
